import { useNavigate } from 'react-router-dom'
import { Title, Text, Stack, Button, Group, Card, Progress, Alert, Loader, Center } from '@mantine/core'
import { useAuthStore } from '../../stores/authStore'
import { calcularPassos, useOnboardingProgress } from './useOnboardingProgress'
import styles from '../_shared/CrudPage.module.css'

/**
 * Wizard de onboarding step-by-step para tenants novos (admin_tenant).
 *
 * 4 passos obrigatórios em ordem (decisão P2 — 2026-05-04):
 * 1. PerfilTenant configurado (CNPJ, razão social, email)
 * 2. Pelo menos 1 Empresa Cliente cadastrada
 * 3. Pelo menos 1 equipamento (via planilha OU manual)
 * 4. Pelo menos 1 PMOC emitido
 *
 * Lê do backend (sem localStorage). Pode ser acessado a qualquer momento.
 */
export default function OnboardingPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const { data: progresso, isLoading } = useOnboardingProgress()

  if (isLoading || !progresso) {
    return (
      <Center h={400}>
        <Loader />
      </Center>
    )
  }

  const passos = calcularPassos(progresso)
  const totalFeitos = passos.filter((p) => p.feito).length
  const pct = Math.round((totalFeitos / passos.length) * 100)
  const proximoPasso = passos.find((p) => !p.feito)
  const tudoFeito = !proximoPasso

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <Title order={1} size="h2">
            🚀 Configurando o sistema
          </Title>
          <Text c="dimmed" size="sm">
            Olá {user?.nomeCompleto?.split(' ')[0] ?? 'Admin'}! Siga estes passos para começar a
            usar o PMOC_123.
          </Text>
        </div>
      </header>

      <Stack gap="md" mt="lg">
        {/* Progresso geral */}
        <Card withBorder padding="lg" radius="md">
          <Group justify="space-between" mb="xs">
            <Text fw={600}>
              Progresso: {totalFeitos} de {passos.length} passos
            </Text>
            <Text size="sm" c="dimmed">
              {pct}%
            </Text>
          </Group>
          <Progress
            value={pct}
            size="lg"
            color={tudoFeito ? 'teal' : 'brand'}
            data-testid="onboarding-progress-bar"
            aria-label={`Progresso do onboarding: ${pct}%`}
          />
        </Card>

        {tudoFeito && (
          <Alert color="teal" title="🎉 Tudo configurado!" data-testid="onboarding-completo">
            Você concluiu o onboarding. O sistema está pronto pra gerar OS automaticamente
            conforme as periodicidades dos planos do PMOC.
            <Group mt="md">
              <Button onClick={() => navigate('/dashboard')}>Ir pro Dashboard</Button>
              <Button variant="light" onClick={() => navigate('/pmoc')}>
                Ver PMOCs
              </Button>
            </Group>
          </Alert>
        )}

        {/* Lista de passos */}
        {passos.map((passo, idx) => {
          const isProximo = !passo.feito && passo === proximoPasso
          return (
            <Card
              key={passo.id}
              withBorder
              padding="lg"
              radius="md"
              data-testid={`onboarding-passo-${passo.id}`}
              style={{
                borderColor: passo.feito
                  ? 'var(--mantine-color-teal-6)'
                  : isProximo
                    ? 'var(--mantine-color-brand-6)'
                    : undefined,
                borderWidth: isProximo ? 2 : 1,
                opacity: passo.feito ? 0.7 : 1,
              }}
            >
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <div style={{ flex: 1 }}>
                  <Group gap="xs" mb={4}>
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                      Passo {idx + 1}
                    </Text>
                    {passo.feito && (
                      <Text size="xs" c="teal" fw={700}>
                        ✓ Concluído
                      </Text>
                    )}
                    {isProximo && (
                      <Text size="xs" c="brand" fw={700}>
                        ➜ Próximo
                      </Text>
                    )}
                  </Group>
                  <Title order={4} mb={4}>
                    {passo.titulo}
                  </Title>
                  <Text size="sm" c="dimmed">
                    {passo.descricao}
                  </Text>
                </div>
                {!passo.feito && (
                  <Button
                    onClick={() => navigate(passo.ctaTo)}
                    variant={isProximo ? 'filled' : 'light'}
                    data-testid={`onboarding-cta-${passo.id}`}
                  >
                    {passo.ctaLabel}
                  </Button>
                )}
              </Group>
            </Card>
          )
        })}

        {/* Atalhos secundários */}
        {!tudoFeito && (
          <Card withBorder padding="md" radius="md" bg="var(--mantine-color-gray-0)">
            <Text size="sm" c="dimmed" mb="xs">
              💡 Dicas
            </Text>
            <ul style={{ margin: 0, fontSize: '0.875rem', color: 'var(--mantine-color-dimmed)' }}>
              <li>
                Você pode pular esta tela e voltar quando quiser. Ela está disponível em{' '}
                <strong>/onboarding</strong>.
              </li>
              <li>
                Para cadastrar muitos sites/equipamentos de uma vez, use os botões{' '}
                <strong>"📥 Importar planilha"</strong> nas listas.
              </li>
              <li>
                Tem dúvidas? Acesse o <strong>Manual de uso</strong> no menu lateral.
              </li>
            </ul>
          </Card>
        )}
      </Stack>
    </div>
  )
}
